import { Box, CircularProgress, IconButton, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { CloseCircle, Copy, InfoCircle, Shop, TickCircle } from "iconsax-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useGetPublicTracking } from "@/api";
import { formatPersianDateLong, formatPersianNumber } from "@/lib/format/persian";
import { resolveMediaUrl } from "@/lib/media";

const STEPS = ["created", "confirmed", "preparing", "shipped", "completed"] as const;

/** Public order tracking page (link sent to the customer by SMS). */
export function TrackingPage({ uuid }: { uuid: string }) {
  const { t } = useTranslation("checkout");
  const theme = useTheme();
  const [copied, setCopied] = useState(false);

  const { data, isLoading } = useGetPublicTracking(uuid, { query: { retry: false } });
  const order = data?.status === 200 ? data.data.data : undefined;

  const isCanceled = order?.status === "canceled";
  const currentStep = order ? STEPS.indexOf(order.status as (typeof STEPS)[number]) : -1;
  const logoUrl = resolveMediaUrl(order?.store_logo_url);

  const copyCode = () => {
    if (!order?.post_tracking_code) return;
    void navigator.clipboard.writeText(order.post_tracking_code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const card = {
    width: "100%",
    bgcolor: "background.paper",
    borderRadius: 2.5,
    border: `1px solid ${theme.palette.divider}`,
    p: { xs: 2, md: 2.5 },
  };

  return (
    <Box
      sx={{
        minHeight: "100dvh",
        bgcolor: "background.default",
        display: "flex",
        justifyContent: "center",
        px: 2,
        py: { xs: 3, md: 6 },
      }}
    >
      <Box
        sx={{ width: "100%", maxWidth: 520, display: "flex", flexDirection: "column", gap: 1.5 }}
      >
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress size={30} />
          </Box>
        ) : !order ? (
          <Box
            sx={{
              ...card,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1.5,
              py: 5,
            }}
          >
            <InfoCircle size={44} variant="Bulk" color={theme.palette.warning.main} />
            <Typography sx={{ fontSize: "0.9375rem", fontWeight: 700 }}>
              {t("tracking.notFound")}
            </Typography>
          </Box>
        ) : (
          <>
            <Box sx={{ ...card, display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  width: 52,
                  height: 52,
                  borderRadius: 1.5,
                  overflow: "hidden",
                  flex: "none",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                }}
              >
                {logoUrl ? (
                  <Box
                    component="img"
                    src={logoUrl}
                    alt=""
                    sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                ) : (
                  <Shop size={26} variant="Bold" color={theme.palette.primary.main} />
                )}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography component="h1" sx={{ fontSize: "0.9375rem", fontWeight: 800 }}>
                  {order.store_name}
                </Typography>
                <Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
                  {t("tracking.orderNumber", { number: formatPersianNumber(order.order_number) })}
                </Typography>
              </Box>
            </Box>

            <Box sx={card}>
              <Typography sx={{ fontSize: "0.84375rem", fontWeight: 800, mb: 2 }}>
                {t("tracking.title")}
              </Typography>
              {isCanceled ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    color: "error.main",
                    fontWeight: 700,
                    fontSize: "0.8125rem",
                  }}
                >
                  <CloseCircle size={22} variant="Bold" color={theme.palette.error.main} />
                  {t("tracking.canceled")}
                </Box>
              ) : (
                <Box component="ol" sx={{ listStyle: "none", m: 0, p: 0 }}>
                  {STEPS.map((step, index) => {
                    const done = index <= currentStep;
                    const isCurrent = index === currentStep;
                    const color = done ? theme.palette.success.main : theme.palette.divider;
                    const isLast = index === STEPS.length - 1;
                    return (
                      <Box
                        component="li"
                        key={step}
                        sx={{ display: "flex", gap: 1.5, minHeight: isLast ? 0 : 48 }}
                      >
                        <Box
                          sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}
                        >
                          <Box
                            sx={{
                              width: 22,
                              height: 22,
                              borderRadius: "50%",
                              flex: "none",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              bgcolor: done ? color : "background.paper",
                              border: `2px solid ${color}`,
                              boxShadow: isCurrent ? `0 0 0 4px ${alpha(color, 0.18)}` : "none",
                            }}
                          >
                            {done ? (
                              <TickCircle
                                size={14}
                                variant="Bold"
                                color={theme.palette.common.white}
                              />
                            ) : null}
                          </Box>
                          {isLast ? null : (
                            <Box
                              sx={{
                                width: 2,
                                flex: 1,
                                my: 0.5,
                                bgcolor: index < currentStep ? color : theme.palette.divider,
                              }}
                            />
                          )}
                        </Box>
                        <Typography
                          sx={{
                            fontSize: "0.8125rem",
                            fontWeight: isCurrent ? 800 : 500,
                            color: done ? "text.primary" : "text.secondary",
                            pt: 0.125,
                          }}
                        >
                          {t(`tracking.steps.${step}`)}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              )}
              <Typography sx={{ fontSize: "0.6875rem", color: "text.secondary", mt: 2 }}>
                {t("tracking.updatedAt", {
                  date: formatPersianDateLong(new Date(order.updated_at)),
                })}
              </Typography>
            </Box>

            {order.post_tracking_code ? (
              <Box sx={{ ...card, display: "flex", alignItems: "center", gap: 1 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: "0.6875rem", color: "text.secondary" }}>
                    {t("tracking.postCode")}
                  </Typography>
                  <Typography
                    dir="ltr"
                    sx={{
                      fontSize: "0.9375rem",
                      fontWeight: 800,
                      textAlign: "start",
                      letterSpacing: 1,
                    }}
                  >
                    {order.post_tracking_code}
                  </Typography>
                </Box>
                <IconButton
                  onClick={copyCode}
                  title={copied ? t("tracking.copied") : t("tracking.copy")}
                >
                  {copied ? (
                    <TickCircle size={20} variant="Bold" color={theme.palette.success.main} />
                  ) : (
                    <Copy size={20} variant="Linear" color={theme.palette.text.secondary} />
                  )}
                </IconButton>
              </Box>
            ) : null}

            <Box sx={card}>
              <Typography sx={{ fontSize: "0.84375rem", fontWeight: 800, mb: 1.25 }}>
                {t("tracking.items")}
              </Typography>
              {order.items.map((item, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    py: 0.75,
                    fontSize: "0.8125rem",
                  }}
                >
                  <Box component="span">{item.product_name}</Box>
                  <Box component="span" sx={{ color: "text.secondary" }}>
                    × {formatPersianNumber(item.quantity)}
                  </Box>
                </Box>
              ))}
              <Box
                sx={{
                  borderTop: `1px solid ${theme.palette.divider}`,
                  mt: 1,
                  pt: 1.25,
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.75,
                }}
              >
                {order.shipping_method ? (
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between", fontSize: "0.75rem" }}
                  >
                    <Box component="span" sx={{ color: "text.secondary" }}>
                      {t("tracking.shippingMethod")}
                    </Box>
                    <Box component="span" sx={{ fontWeight: 600 }}>
                      {t(`tracking.methods.${order.shipping_method}`, {
                        defaultValue: order.shipping_method,
                      })}
                    </Box>
                  </Box>
                ) : null}
                <Box sx={{ display: "flex", justifyContent: "space-between", fontSize: "0.75rem" }}>
                  <Box component="span" sx={{ color: "text.secondary" }}>
                    {t("tracking.paymentStatus")}
                  </Box>
                  <Box
                    component="span"
                    sx={{
                      fontWeight: 600,
                      color: order.payment_status === "paid" ? "success.main" : "warning.main",
                    }}
                  >
                    {order.payment_status === "paid" ? t("tracking.paid") : t("tracking.unpaid")}
                  </Box>
                </Box>
              </Box>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
}
